import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Card,
  Checkbox,
  Col,
  Collapse,
  Flex,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import { WizardService } from "../../services/WizardService";
import { AppRoutes } from "../../AppRoutes";
import { Sdk, SdkCategory } from "../../types/sdk";
import { Plugin } from "../../types/plugin";
import { Platform } from "../../types/platform";
import {
  BadgeType,
  CoreSplitResult,
  IntegrationType,
  MediationInfo,
  NativeRequirement,
  NativeSdkCategory,
  OutOfScopeNetwork,
  PlatformSelection,
  SdkInfo,
  SdksRequest,
  SpmCoverageResponse,
  SpmDependency,
  UncoveredEntity,
  emptyPlatformSelection,
} from "../../types/nativeIntegration";
import { SpmIntegrationRecord } from "../../types/spmIntegrationInfo";
import { CodeBlock } from "./CodeBlock";
import { ResponsiveCard } from "./ResponsiveCard";
import { safeHref } from "./safeHref";

const { Text } = Typography;

/**
 * SDK-562 Phase 6 (Requirement W) — Integration Preview built on the new 3-step
 * `/v4/{android,ios,unity}` API. Replaces `MediationIntegrationPreview`/`PluginIntegrationPreview`
 * as the component actually rendered on `SdkViewPage`/`PluginViewPage` (those two old files are
 * left on disk untouched — deletion is a separate, human-approved follow-up).
 */

/* ---------------------------------------------------------------------------------------------
 * Selection model
 *
 * Step 1 (`mediations`) returns a flat `MediationInfo[]` covering every selectable item
 * (mediation core + ad networks + services, disambiguated by `.category`). Each item carries its
 * own `VersionInfo[]`; there is no per-item numeric id, only `.name`. The only numeric ids in the
 * whole step-1 shape are `VersionInfo.id` — so "the user's selection" is modelled here as, per
 * item (see `mediationKey`), which version id (if any) they picked. `PlatformSelection.{mediations,
 * networks,services}` is then just those chosen version ids, bucketed by the parent item's
 * category. That same `PlatformSelection` is reused unmodified for both step 2 (`sdks`, an
 * informational resolved-list/warnings preview) and step 3 (`dependencies`, the generated code) —
 * there is no second round of selection on the step-2 response.
 *
 * **Corrected 2026-09-18 (KI-24)**: the sentence above used to end "...is modelled here as, per
 * item name, which version id they picked", and that name-keyed model was the defect. It is kept
 * as history, and named as wrong: `.name` is NOT unique within a step-1 payload. The measured
 * `max`/android case (`GET /v4/android/13.6.4/mediations?mediation=max`) returns two items that
 * share `name` AND `category` and differ only in their `versions` list, so both collapsed into one
 * `SelectionChoices` slot — last write won the default, both cards' `<Select>`/`<Checkbox>` drove
 * the same slot, and the anchor SDK id was never selected at all. Identity is now `mediationKey`.
 * ------------------------------------------------------------------------------------------- */

/**
 * Identity of one selectable step-1/step-2 item, used as its `SelectionChoices` slot AND as its
 * React `key` everywhere it is rendered. Added 2026-09-18 for KI-24 (b) — the frontend half of that
 * fix, which has to stand on its own whether or not the backend half (dedup by `status` in
 * `findCompatibleMediations`) ever lands.
 *
 * Composite of `category` + `name` + the item's version ids. Note what this deliberately is NOT:
 * `reports/known-issues.md`'s KI-24 "Fix when scheduled" proposes `${category}:${name}`, and that
 * is **insufficient** — the duplicate actually observed there is two `category: 1` items both named
 * `max`, so category+name still collides on the one payload this exists to fix. The version-id list
 * is the only field that distinguishes them, so it is part of the identity.
 *
 * Platform is intentionally absent: every `SelectionChoices` map in this file is already scoped to
 * one platform (`UnityPanel` keeps `androidChoices`/`iosChoices` as separate state), so an
 * android/iOS pair sharing a name can never meet inside one map.
 *
 * Residual case, stated rather than hidden: two items identical in category, name and version ids
 * are indistinguishable by any pure function of the payload, and would still share a slot. Such a
 * pair carries no distinguishable selectable content, and React's duplicate-key warning stays the
 * detector if the API ever emits one.
 */
const mediationKey = (item: MediationInfo): string =>
  `${item.category}:${item.name}:${item.versions.map((v) => v.id).join(",")}`;

/** Selected version id per `mediationKey(item)`; absent = item excluded from the selection. */
type SelectionChoices = Record<string, number | undefined>;

/**
 * Per-step error message for a panel's 3-step fetch chain. Kept as three independent fields
 * (rather than one shared `error`) so a step-3 success does not clobber a step-1 failure still on
 * screen, and vice versa — each step's `Alert` reads only its own field. Step 2 (`sdks`) used to
 * swallow its error silently (catch emptied `resolved` with no message), which made a failed
 * request indistinguishable from a genuine "0 networks" result — fixed so all three steps report
 * the same way.
 */
type StepErrors = { mediations: string | null; sdks: string | null; dependencies: string | null };
const NO_STEP_ERRORS: StepErrors = { mediations: null, sdks: null, dependencies: null };

const stepErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.message || fallback;

const CATEGORY_LABELS: Record<number, string> = {
  [NativeSdkCategory.MEDIATION]: "Mediation",
  [NativeSdkCategory.AD_NETWORK]: "Ad Networks",
  [NativeSdkCategory.SERVICE]: "Services",
  [NativeSdkCategory.OTHER]: "Other",
};

const recommendedVersionId = (item: MediationInfo): number | undefined => {
  const recommended = item.versions.find((v) =>
    (v.badges ?? []).some((b) => b.type === BadgeType.RECOMMENDED)
  );
  return (recommended ?? item.versions[0])?.id;
};

/** Mirrors the old `SelectableSdk` default: everything but `OPTIONAL` starts selected. */
const defaultChoices = (items: MediationInfo[]): SelectionChoices => {
  const choices: SelectionChoices = {};
  items.forEach((item) => {
    if (item.requirement === NativeRequirement.OPTIONAL) return;
    const versionId = recommendedVersionId(item);
    if (versionId !== undefined) choices[mediationKey(item)] = versionId;
  });
  return choices;
};

const deriveSelection = (
  items: MediationInfo[],
  choices: SelectionChoices
): PlatformSelection => {
  const selection = emptyPlatformSelection();
  items.forEach((item) => {
    const versionId = choices[mediationKey(item)];
    if (versionId === undefined) return;
    if (item.category === NativeSdkCategory.MEDIATION) {
      selection.mediations.push(versionId);
    } else if (item.category === NativeSdkCategory.AD_NETWORK) {
      selection.networks.push(versionId);
    } else if (item.category === NativeSdkCategory.SERVICE) {
      selection.services.push(versionId);
    }
  });
  return selection;
};

/**
 * Combines the step-1 (`mediations`) selection with the user's choices over the step-2 (`sdks`)
 * response into the selection step 3 (`dependencies`) actually sends. Kept as a separate merge
 * rather than folding network/service choices into the same `choices` map step 2 reads from:
 * step 2's own request must be derived from step-1 choices ONLY. If it also depended on the
 * network/service choices, then selecting a network would recompute step 2's request, which
 * returns a (new, but equivalent) resolved list, whose defaults would touch the same choices
 * again — selection -> step 2 -> resolved -> choices -> selection -> step 2 -> ... forever.
 * `mediations` is taken from `mediationSelection` only (never from `resolvedSelection`) so a
 * step-2 response that happens to echo a MEDIATION-category row can never double up the id.
 */
const mergeSelections = (
  mediationSelection: PlatformSelection,
  resolvedSelection: PlatformSelection
): PlatformSelection => ({
  mediations: mediationSelection.mediations,
  networks: resolvedSelection.networks,
  services: resolvedSelection.services,
});

/**
 * `sdk.status`/anchor `.status` -> `mediation` query param. "appodeal" is the implicit backend
 * default, so it is omitted rather than sent explicitly — matches the truthy check already done
 * inside `AndroidWizardApiComponent`/`IosWizardApiComponent` (WizardService.ts).
 */
const mediationParam = (status?: string): string | undefined =>
  status && status !== "appodeal" ? status : undefined;

/* ---------------------------------------------------------------------------------------------
 * Selection UI
 * ------------------------------------------------------------------------------------------- */

const MediationSelector: React.FC<{
  items: MediationInfo[];
  choices: SelectionChoices;
  /**
   * Corrected 2026-09-18 (KI-24): first argument used to be `name: string` (`MediationInfo.name`).
   * It is now the `mediationKey(item)` composite — the caller stores it verbatim as the
   * `SelectionChoices` slot, so it must be the same string `defaultChoices`/`deriveSelection` use.
   */
  onChange: (itemKey: string, versionId: number | undefined) => void;
}> = ({ items, choices, onChange }) => {
  const categories = [
    NativeSdkCategory.MEDIATION,
    NativeSdkCategory.AD_NETWORK,
    NativeSdkCategory.SERVICE,
    NativeSdkCategory.OTHER,
  ];

  if (items.length === 0) {
    return <Text type="secondary">No selectable SDKs.</Text>;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {categories.map((category) => {
        const group = items.filter((item) => item.category === category);
        if (group.length === 0) return null;
        return (
          <div key={category}>
            <Text type="secondary">{CATEGORY_LABELS[category]}</Text>
            <Flex wrap="wrap" gap="small" style={{ marginTop: 8 }}>
              {group.map((item) => {
                const itemKey = mediationKey(item);
                const selectedVersionId = choices[itemKey];
                const isSelected = selectedVersionId !== undefined;
                const isRequired = item.requirement === NativeRequirement.REQUIRED;
                const hasVersions = item.versions.length > 0;
                return (
                  // Corrected 2026-09-18 (KI-24): `key` was `item.name`, which duplicated inside a
                  // single category group (`max`/android returns two `category: 1` items named
                  // `max`) and fired React's duplicate-key warning from this exact element.
                  <Card size="small" key={itemKey} style={{ minWidth: 220 }}>
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      <Checkbox
                        checked={isSelected}
                        disabled={isRequired || !hasVersions}
                        onChange={(e) =>
                          onChange(
                            itemKey,
                            e.target.checked ? recommendedVersionId(item) : undefined
                          )
                        }
                      >
                        {item.displayName}
                        {isRequired && (
                          <Tag color="blue" style={{ marginLeft: 4 }}>
                            required
                          </Tag>
                        )}
                      </Checkbox>
                      {isSelected && hasVersions && (
                        // Intentionally shows the full version list on this admin surface (the old
                        // "Recommended only" narrowing toggle was removed as dead UI — it had no
                        // effect on the generated preview). The initial/default selection is still
                        // badge-driven via `recommendedVersionId`/`defaultChoices`, unaffected here.
                        <Select
                          size="small"
                          style={{ width: "100%" }}
                          value={selectedVersionId}
                          onChange={(value: number) => onChange(itemKey, value)}
                          options={item.versions.map((v) => {
                            const badges = v.badges ?? [];
                            return {
                              value: v.id,
                              label:
                                v.name +
                                (badges.length > 0
                                  ? " · " +
                                    badges
                                      .map((b) => b.message)
                                      .filter((m): m is string => !!m)
                                      .join(", ")
                                  : ""),
                            };
                          })}
                        />
                      )}
                      {!hasVersions && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          No versions available
                        </Text>
                      )}
                    </Space>
                  </Card>
                );
              })}
            </Flex>
          </div>
        );
      })}
    </Space>
  );
};

/** Surfaces `SdkInfo.warning` from the step-2 (`sdks`) response — informational, never blocking. */
const ResolvedWarnings: React.FC<{ sdks: SdkInfo[] }> = ({ sdks }) => {
  const warned = sdks.filter((s) => s.warning);
  if (warned.length === 0) return null;
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {/* Corrected 2026-09-18 (KI-24): `key` was `s.name`. KI-24 filed this as the latent second
          instance of the same shape as the `MediationSelector` card above — unlike that one it was
          never observed firing, but `SdkInfo extends MediationInfo` so it is keyed off the same
          non-unique field and is fixed with the same helper rather than left to be found later. */}
      {warned.map((s) => (
        <Alert
          key={mediationKey(s)}
          type="warning"
          showIcon
          message={s.displayName}
          description={s.warning}
        />
      ))}
    </Space>
  );
};

/**
 * Composite key shared by `SpmDependency` (the raw wire row) and `SpmIntegrationRecord` (the
 * materialized admin row) — `url`+`product`+`version` together identify "the same resolved Swift
 * package product" in both shapes, so this is the join key `useSpmIntegrationIndex` below builds
 * its lookup on. Duck-typed on purpose (an inline shape, not either named type) so the same
 * function keys both a `SpmDependency` and a `SpmIntegrationRecord` without a cast.
 */
const spmIntegrationKey = (row: { url: string; product: string; version: string }): string =>
  `${row.url}|${row.product}|${row.version}`;

type SpmIntegrationIndex = Map<string, SpmIntegrationRecord>;

/**
 * Fetches the MATERIALIZED SPM table (`GET v4/spm/integrations`, `WizardService.SpmIntegration`)
 * and indexes it by `spmIntegrationKey`, purely so `SpmDependencyTable` below can enrich its
 * DISPLAY — the raw `SpmDependency[]` it renders stays byte-identical to what
 * `GET /v4/ios/{version}/dependencies?integration_type=spm` sent (see `SpmDependencyDto`'s KDoc:
 * materialization concerns "must never leak into this consumer-facing payload"). This hook never
 * touches that payload; it only builds a side lookup table used purely for rendering.
 *
 * Backend `MAX_PAGE_SIZE` is 100 (`SpmIntegrationInfoService.MAX_PAGE_SIZE`) and the table holds
 * ~226 rows store-wide, so this pages at `per=100` and follows `PageResult.metadata` until
 * exhausted — capped at 5 pages (2000 rows) defensively so a runaway `total` can never turn a
 * display-only enrichment into an unbounded fetch loop. `mediation`, when the caller has one,
 * narrows the query the same way `SpmPage.tsx`'s own mediation filter does.
 *
 * Failure is silent-ish by design, per this table's whole reason for existing: a rejected fetch
 * (any page) aborts the loop and leaves `index` as it was — `Map()` on first load, so every
 * dependency row falls back to rendering exactly as it did before this enrichment existed. This
 * table must never break or block the preview around it over a diagnostics-only lookup.
 *
 * Same monotonic-request-id + "bail out after unmount" idiom as the coverage effect in
 * `IntegrationPreview` below (the `coverageRequestId` ref) — a slow page 3 for a superseded
 * `mediation` (or one that resolves after the component unmounted) must never overwrite a newer
 * index or call `setState` on an unmounted component.
 */
const useSpmIntegrationIndex = (mediation?: string): SpmIntegrationIndex => {
  const [index, setIndex] = useState<SpmIntegrationIndex>(new Map());
  const requestId = useRef(0);

  useEffect(() => {
    const thisRequestId = ++requestId.current;
    let cancelled = false;
    const MAX_PAGES = 5;

    (async () => {
      const collected: SpmIntegrationRecord[] = [];
      for (let page = 1; page <= MAX_PAGES; page++) {
        const params = new URLSearchParams({ page: String(page), per: "100" });
        if (mediation) params.set("mediation", mediation);
        let response;
        try {
          response = await WizardService.SpmIntegration.all(params);
        } catch {
          // Leave `index` untouched — see this hook's KDoc: a failure must never partially
          // populate the index, it must look exactly like "no enrichment available yet".
          return;
        }
        if (cancelled || thisRequestId !== requestId.current) return;
        collected.push(...(response.items ?? []));
        const per = response.metadata.per || 100;
        const total = response.metadata.total ?? collected.length;
        // Stop as soon as everything is collected, or a short page signals the last one — either
        // makes the `MAX_PAGES` cap above a defensive backstop, not the normal exit path.
        if (collected.length >= total || (response.items ?? []).length < per) break;
      }
      if (cancelled || thisRequestId !== requestId.current) return;
      const next: SpmIntegrationIndex = new Map();
      collected.forEach((record) => next.set(spmIntegrationKey(record), record));
      setIndex(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [mediation]);

  return index;
};

/** Single source of truth for "where does this materialized row's anchor live". Duplicated from
 * the identical `targetHref` in `SpmPage.tsx` rather than extracted to a shared module — this
 * task's change is scoped to this file plus a type-only edit, so a new shared file was not an
 * option. Keep the two in sync by hand if either changes. */
const spmTargetHref = (record: SpmIntegrationRecord): string =>
  record.targetKind === "adapter"
    ? `/wizard/adapter/${record.targetId}`
    : `/wizard/sdk/${record.targetId}`;

/**
 * `GET /v4/ios/{version}/dependencies?integration_type=spm` — flat JSON, not source code.
 * `mediation` is passed through only to narrow the enrichment lookup above (`useSpmIntegrationIndex`);
 * it is never sent to the dependencies endpoint itself, which already has its own `mediation` param
 * at the `IosPanel` call site.
 */
const SpmDependencyTable: React.FC<{ dependencies: SpmDependency[]; mediation?: string }> = ({
  dependencies,
  mediation,
}) => {
  const index = useSpmIntegrationIndex(mediation);

  return (
    <Table
      size="small"
      pagination={false}
      rowKey={(row: SpmDependency) => row.url + "|" + row.product}
      dataSource={dependencies}
      columns={[
        {
          title: "Product",
          dataIndex: "product",
          render: (product: string, row: SpmDependency) => {
            const match = index.get(spmIntegrationKey(row));
            if (!match) return product;
            return (
              <Space size={4}>
                <Link to={spmTargetHref(match)}>{product}</Link>
                <Tag color={match.targetKind === "adapter" ? "geekblue" : "purple"}>
                  {match.targetKind}
                </Tag>
              </Space>
            );
          },
        },
        {
          title: "Version",
          dataIndex: "version",
          render: (version: string, row: SpmDependency) => {
            const match = index.get(spmIntegrationKey(row));
            if (!match) return version;
            return (
              <Space direction="vertical" size={0}>
                <Text>{match.targetVersion}</Text>
                <Tooltip title="Raw SPM git tag, verbatim — the value pasted into Xcode, never decoded.">
                  <Text code type="secondary" style={{ fontSize: 12 }}>
                    {version}
                  </Text>
                </Tooltip>
              </Space>
            );
          },
        },
        {
          title: "URL",
          dataIndex: "url",
          ellipsis: true,
          render: (url: string) => {
            const href = safeHref(url);
            return href ? (
              <a href={href} target="_blank" rel="noreferrer">
                {url}
              </a>
            ) : (
              <Text>{url}</Text>
            );
          },
        },
      ]}
    />
  );
};

/* ---------------------------------------------------------------------------------------------
 * Per-platform panels — each owns its own 3-step fetch chain.
 * ------------------------------------------------------------------------------------------- */

const AndroidPanel: React.FC<{
  version: string;
  mediation?: string;
  onSelectionChange: (selection: PlatformSelection) => void;
}> = ({ version, mediation, onSelectionChange }) => {
  const [language, setLanguage] = useState<"kt" | "groovy">("kt");
  const [mediations, setMediations] = useState<MediationInfo[]>([]);
  const [choices, setChoices] = useState<SelectionChoices>({});
  const [resolved, setResolved] = useState<SdkInfo[]>([]);
  // Choices over the step-2 (`sdks`) response — the ad networks/services the user picked among
  // what step 2 resolved. Kept separate from `choices` above; see `mergeSelections`'s comment for
  // why merging them into one map would loop.
  const [resolvedChoices, setResolvedChoices] = useState<SelectionChoices>({});
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepErrors, setStepErrors] = useState<StepErrors>(NO_STEP_ERRORS);

  // Per-step request-id guards (same pattern as the coverage effect below): each fetch bumps its
  // own ref and every callback bails out if a newer request for that same step has since been
  // issued. Without this, e.g. toggling a control mid-flight lets a slow response for the OLD
  // selection land after a fast response for the NEW one and silently overwrite it.
  const mediationsRequestId = useRef(0);
  const sdksRequestId = useRef(0);
  const dependenciesRequestId = useRef(0);

  // Step 1 — refetches whenever version/mediation change.
  const loadMediations = useCallback(() => {
    const requestId = ++mediationsRequestId.current;
    setLoading(true);
    setStepErrors((prev) => ({ ...prev, mediations: null }));
    WizardService.Android.mediations(version, { mediation })
      .then((response) => {
        if (requestId !== mediationsRequestId.current) return;
        setMediations(response.mediations);
        setChoices(defaultChoices(response.mediations));
      })
      .catch((err: any) => {
        if (requestId !== mediationsRequestId.current) return;
        // Reset the list/selection too — leaving the previous (now invalid) `mediations`/`choices`
        // in place would let the user pick an item that does not exist for the current version.
        // Also clear everything the (now-superseded) downstream steps wrote — otherwise a stale
        // Podfile/Gradle snippet (or a stale step-3 error) from the previous version stays on
        // screen underneath the new step-1 error.
        setMediations([]);
        setChoices({});
        setResolved([]);
        setResolvedChoices({});
        setCode("");
        setStepErrors({
          ...NO_STEP_ERRORS,
          mediations: stepErrorMessage(err, "Failed to load mediations"),
        });
      })
      .finally(() => {
        if (requestId !== mediationsRequestId.current) return;
        setLoading(false);
      });
  }, [version, mediation]);

  useEffect(() => {
    loadMediations();
  }, [loadMediations]);

  // Step-1-only selection. Deliberately excludes `resolvedChoices` — this is what step 2 requests
  // with; see `mergeSelections`'s comment for why step 2's own request must never depend on the
  // step-2 response's own choices.
  const mediationSelection = useMemo(
    () => deriveSelection(mediations, choices),
    [mediations, choices]
  );

  // Step 2 — resolved ad networks/services (now user-selectable, not just informational) plus
  // warnings. Uses `mediationSelection`, never `fullSelection` — see `mergeSelections`.
  useEffect(() => {
    // Bump the guard on every path that starts a new "attempt" for this step — including the
    // early return below — so a still in-flight response from a previous (now-superseded)
    // attempt can never pass the `requestId === ref.current` check and overwrite state.
    const requestId = ++sdksRequestId.current;
    if (mediations.length === 0) {
      setResolved([]);
      setResolvedChoices({});
      setStepErrors((prev) => ({ ...prev, sdks: null }));
      return;
    }
    WizardService.Android.sdks(version, mediationSelection)
      .then((response) => {
        if (requestId !== sdksRequestId.current) return;
        setResolved(response.sdks);
        // Fresh response -> fresh defaults. A stale `resolvedChoices` keyed by names from the
        // previous response could reference version ids that no longer exist for this one.
        setResolvedChoices(defaultChoices(response.sdks));
        setStepErrors((prev) => ({ ...prev, sdks: null }));
      })
      .catch((err: any) => {
        if (requestId !== sdksRequestId.current) return;
        // Clear rather than leave stale — but the empty result must not look like a genuine "0
        // networks" answer, so surface the failure via `stepErrors.sdks` same as steps 1 and 3.
        setResolved([]);
        setResolvedChoices({});
        setStepErrors((prev) => ({
          ...prev,
          sdks: stepErrorMessage(err, "Failed to load resolved SDKs"),
        }));
      });
  }, [version, mediationSelection, mediations.length]);

  const resolvedSelection = useMemo(
    () => deriveSelection(resolved, resolvedChoices),
    [resolved, resolvedChoices]
  );

  // What step 3 and `onSelectionChange` actually use — step-1 mediations merged with the user's
  // network/service picks over the step-2 response.
  const fullSelection = useMemo(
    () => mergeSelections(mediationSelection, resolvedSelection),
    [mediationSelection, resolvedSelection]
  );

  useEffect(() => {
    onSelectionChange(fullSelection);
  }, [fullSelection, onSelectionChange]);

  // Step 3
  useEffect(() => {
    // Same early-return guard-bump rationale as step 2 above.
    const requestId = ++dependenciesRequestId.current;
    if (mediations.length === 0) {
      setCode("");
      setStepErrors((prev) => ({ ...prev, dependencies: null }));
      return;
    }
    WizardService.Android.dependencies(version, language, fullSelection, { mediation })
      .then((response) => {
        if (requestId !== dependenciesRequestId.current) return;
        setCode(response);
        setStepErrors((prev) => ({ ...prev, dependencies: null }));
      })
      .catch((err: any) => {
        if (requestId !== dependenciesRequestId.current) return;
        // Drop the previous generation's snippet along with raising the error. Leaving it rendered
        // puts a copyable code block underneath a "failed" alert, and it describes the selection
        // the user just moved away from — the worst possible combination, because it looks current.
        setCode("");
        setStepErrors((prev) => ({
          ...prev,
          dependencies: stepErrorMessage(err, "Failed to load dependencies"),
        }));
      });
  }, [version, language, fullSelection, mediation, mediations.length]);

  // Corrected 2026-09-18 (KI-24): the slot is `mediationKey(item)`, not `MediationInfo.name`.
  const onChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setChoices((prev) => ({ ...prev, [itemKey]: versionId }));

  const onResolvedChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setResolvedChoices((prev) => ({ ...prev, [itemKey]: versionId }));

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Segmented
        value={language}
        onChange={(value) => setLanguage(value as "kt" | "groovy")}
        aria-label="Code Language"
        options={[
          { label: "Kotlin DSL", value: "kt" },
          { label: "Groovy", value: "groovy" },
        ]}
      />
      {stepErrors.mediations && <Alert type="error" showIcon message={stepErrors.mediations} />}
      <Spin spinning={loading}>
        <MediationSelector items={mediations} choices={choices} onChange={onChoiceChange} />
      </Spin>
      {/* Step-2 (`sdks`) result, now selectable — reuses the same `MediationSelector` over its own
          `resolvedChoices`, not `choices` above. See `mergeSelections`'s comment for why. */}
      {stepErrors.sdks && <Alert type="error" showIcon message={stepErrors.sdks} />}
      <MediationSelector items={resolved} choices={resolvedChoices} onChange={onResolvedChoiceChange} />
      <ResolvedWarnings sdks={resolved} />
      {stepErrors.dependencies && <Alert type="error" showIcon message={stepErrors.dependencies} />}
      {/* Corrected 2026-09-18 (KI-11): CodeBlock's SyntaxHighlighter used to be hardcoded to the
          "1c" hljs grammar regardless of this prop (see CodeBlock.tsx) — `language` here was only
          passed for the xml-format branch / future correctness. That is fixed now: `language` is
          passed through to SyntaxHighlighter and actually drives highlighting. */}
      <CodeBlock language={language === "kt" ? "kotlin" : "groovy"}>{code}</CodeBlock>
    </Space>
  );
};

const IosPanel: React.FC<{
  version: string;
  mediation?: string;
  integrationType: IntegrationType;
  onIntegrationTypeChange: (type: IntegrationType) => void;
  onSelectionChange: (selection: PlatformSelection) => void;
}> = ({ version, mediation, integrationType, onIntegrationTypeChange, onSelectionChange }) => {
  const [mediations, setMediations] = useState<MediationInfo[]>([]);
  const [choices, setChoices] = useState<SelectionChoices>({});
  const [resolved, setResolved] = useState<SdkInfo[]>([]);
  // Choices over the step-2 (`sdks`) response — see the identical field in `AndroidPanel`.
  const [resolvedChoices, setResolvedChoices] = useState<SelectionChoices>({});
  const [code, setCode] = useState("");
  const [spmDeps, setSpmDeps] = useState<SpmDependency[]>([]);
  const [loading, setLoading] = useState(false);
  const [stepErrors, setStepErrors] = useState<StepErrors>(NO_STEP_ERRORS);

  // Per-step request-id guards — see the identical comment in `AndroidPanel`. Especially load-
  // bearing here: toggling CocoaPods<->SPM mid-flight must not let the losing integration type's
  // late response overwrite the winning one's state under the wrong label.
  const mediationsRequestId = useRef(0);
  const sdksRequestId = useRef(0);
  const dependenciesRequestId = useRef(0);

  // Step 1 — `integrationType` is a dependency here, so switching CocoaPods<->SPM restarts the
  // whole chain from step 1 (different integration types can offer different SDKs/versions), not
  // just a step-3 re-fetch.
  const loadMediations = useCallback(() => {
    const requestId = ++mediationsRequestId.current;
    setLoading(true);
    setStepErrors((prev) => ({ ...prev, mediations: null }));
    WizardService.Ios.mediations(version, { mediation, integrationType })
      .then((response) => {
        if (requestId !== mediationsRequestId.current) return;
        setMediations(response.mediations);
        setChoices(defaultChoices(response.mediations));
      })
      .catch((err: any) => {
        if (requestId !== mediationsRequestId.current) return;
        // Reset the list/selection too — leaving the previous (now invalid) `mediations`/`choices`
        // in place would let the user pick an item that does not exist for the current version.
        // Also clear everything the (now-superseded) downstream steps wrote — otherwise a stale
        // Podfile/SPM snippet (or a stale step-3 error) from the previous version stays on screen
        // underneath the new step-1 error.
        setMediations([]);
        setChoices({});
        setResolved([]);
        setResolvedChoices({});
        setCode("");
        setSpmDeps([]);
        setStepErrors({
          ...NO_STEP_ERRORS,
          mediations: stepErrorMessage(err, "Failed to load mediations"),
        });
      })
      .finally(() => {
        if (requestId !== mediationsRequestId.current) return;
        setLoading(false);
      });
  }, [version, mediation, integrationType]);

  useEffect(() => {
    loadMediations();
  }, [loadMediations]);

  // Step-1-only selection — see the identical `mediationSelection` in `AndroidPanel` and
  // `mergeSelections`'s comment for why step 2 must never depend on the step-2 response's choices.
  const mediationSelection = useMemo(
    () => deriveSelection(mediations, choices),
    [mediations, choices]
  );

  // Step 2
  useEffect(() => {
    // Bump the guard on every path that starts a new "attempt" for this step — including the
    // early return below — so a still in-flight response from a previous (now-superseded)
    // attempt can never pass the `requestId === ref.current` check and overwrite state.
    const requestId = ++sdksRequestId.current;
    if (mediations.length === 0) {
      setResolved([]);
      setResolvedChoices({});
      setStepErrors((prev) => ({ ...prev, sdks: null }));
      return;
    }
    WizardService.Ios.sdks(version, mediationSelection, { integrationType })
      .then((response) => {
        if (requestId !== sdksRequestId.current) return;
        setResolved(response.sdks);
        // Fresh response -> fresh defaults; see the identical comment in `AndroidPanel`.
        setResolvedChoices(defaultChoices(response.sdks));
        setStepErrors((prev) => ({ ...prev, sdks: null }));
      })
      .catch((err: any) => {
        if (requestId !== sdksRequestId.current) return;
        // Clear rather than leave stale — but the empty result must not look like a genuine "0
        // networks" answer (this is the exact bug that hid the SPM step-2 500), so surface the
        // failure via `stepErrors.sdks` same as steps 1 and 3.
        setResolved([]);
        setResolvedChoices({});
        setStepErrors((prev) => ({
          ...prev,
          sdks: stepErrorMessage(err, "Failed to load resolved SDKs"),
        }));
      });
  }, [version, mediationSelection, integrationType, mediations.length]);

  const resolvedSelection = useMemo(
    () => deriveSelection(resolved, resolvedChoices),
    [resolved, resolvedChoices]
  );

  // What step 3 and `onSelectionChange` actually use.
  const fullSelection = useMemo(
    () => mergeSelections(mediationSelection, resolvedSelection),
    [mediationSelection, resolvedSelection]
  );

  useEffect(() => {
    onSelectionChange(fullSelection);
  }, [fullSelection, onSelectionChange]);

  // Step 3 — `cocoapods` (default) resolves to a Podfile snippet (string); `spm` resolves to a
  // flat `SpmDependency[]`.
  useEffect(() => {
    // Same early-return guard-bump rationale as step 2 above.
    const requestId = ++dependenciesRequestId.current;
    if (mediations.length === 0) {
      setCode("");
      setSpmDeps([]);
      setStepErrors((prev) => ({ ...prev, dependencies: null }));
      return;
    }
    WizardService.Ios.dependencies(version, fullSelection, { mediation, integrationType })
      .then((response) => {
        if (requestId !== dependenciesRequestId.current) return;
        if (integrationType === "spm" && Array.isArray(response)) {
          setSpmDeps(response);
          setCode("");
        } else {
          setCode(typeof response === "string" ? response : "");
          setSpmDeps([]);
        }
        setStepErrors((prev) => ({ ...prev, dependencies: null }));
      })
      .catch((err: any) => {
        if (requestId !== dependenciesRequestId.current) return;
        // Both outputs go, not just the one the current `integrationType` renders: the toggle can
        // flip between cocoapods and SPM while a request is in flight, so clearing only the active
        // one leaves the other populated and ready to reappear on the next toggle.
        setCode("");
        setSpmDeps([]);
        setStepErrors((prev) => ({
          ...prev,
          dependencies: stepErrorMessage(err, "Failed to load dependencies"),
        }));
      });
  }, [version, fullSelection, mediation, integrationType, mediations.length]);

  // Corrected 2026-09-18 (KI-24): the slot is `mediationKey(item)`, not `MediationInfo.name`.
  const onChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setChoices((prev) => ({ ...prev, [itemKey]: versionId }));

  const onResolvedChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setResolvedChoices((prev) => ({ ...prev, [itemKey]: versionId }));

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Segmented
        value={integrationType}
        onChange={(value) => onIntegrationTypeChange(value as IntegrationType)}
        aria-label="iOS Integration Type"
        options={[
          { label: "CocoaPods", value: "cocoapods" },
          { label: "SPM", value: "spm" },
        ]}
      />
      {stepErrors.mediations && <Alert type="error" showIcon message={stepErrors.mediations} />}
      <Spin spinning={loading}>
        <MediationSelector items={mediations} choices={choices} onChange={onChoiceChange} />
      </Spin>
      {/* Step-2 (`sdks`) result, now selectable — see the identical block in `AndroidPanel`. */}
      {stepErrors.sdks && <Alert type="error" showIcon message={stepErrors.sdks} />}
      <MediationSelector items={resolved} choices={resolvedChoices} onChange={onResolvedChoiceChange} />
      <ResolvedWarnings sdks={resolved} />
      {stepErrors.dependencies && <Alert type="error" showIcon message={stepErrors.dependencies} />}
      {integrationType === "spm" ? (
        <SpmDependencyTable dependencies={spmDeps} mediation={mediation} />
      ) : (
        <CodeBlock language="ruby">{code}</CodeBlock>
      )}
    </Space>
  );
};

const UnityPanel: React.FC<{
  version: string;
  onSelectionChange: (request: SdksRequest) => void;
}> = ({ version, onSelectionChange }) => {
  const [androidMediations, setAndroidMediations] = useState<MediationInfo[]>([]);
  const [iosMediations, setIosMediations] = useState<MediationInfo[]>([]);
  const [androidChoices, setAndroidChoices] = useState<SelectionChoices>({});
  const [iosChoices, setIosChoices] = useState<SelectionChoices>({});
  const [resolved, setResolved] = useState<{ android: SdkInfo[]; ios: SdkInfo[] } | null>(null);
  // Choices over the step-2 (`sdks`) response, per platform — see the identical fields in
  // `AndroidPanel`/`IosPanel`. Unity's two platform selections stay independent, same as the rest
  // of this component's state.
  const [androidResolvedChoices, setAndroidResolvedChoices] = useState<SelectionChoices>({});
  const [iosResolvedChoices, setIosResolvedChoices] = useState<SelectionChoices>({});
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepErrors, setStepErrors] = useState<StepErrors>(NO_STEP_ERRORS);

  // Per-step request-id guards — see the identical comment in `AndroidPanel`.
  const mediationsRequestId = useRef(0);
  const sdksRequestId = useRef(0);
  const dependenciesRequestId = useRef(0);

  const loadMediations = useCallback(() => {
    const requestId = ++mediationsRequestId.current;
    setLoading(true);
    setStepErrors((prev) => ({ ...prev, mediations: null }));
    WizardService.UnityWizard.mediations(version)
      .then((response) => {
        if (requestId !== mediationsRequestId.current) return;
        setAndroidMediations(response.android);
        setIosMediations(response.ios);
        setAndroidChoices(defaultChoices(response.android));
        setIosChoices(defaultChoices(response.ios));
      })
      .catch((err: any) => {
        if (requestId !== mediationsRequestId.current) return;
        // Reset both platforms' list/selection too — leaving the previous (now invalid) values in
        // place would let the user pick an item that does not exist for the current version. Also
        // clear everything the (now-superseded) downstream steps wrote — otherwise a stale
        // generated snippet (or a stale step-3 error) from the previous version stays on screen
        // underneath the new step-1 error.
        setAndroidMediations([]);
        setIosMediations([]);
        setAndroidChoices({});
        setIosChoices({});
        setResolved(null);
        setAndroidResolvedChoices({});
        setIosResolvedChoices({});
        setCode("");
        setStepErrors({
          ...NO_STEP_ERRORS,
          mediations: stepErrorMessage(err, "Failed to load mediations"),
        });
      })
      .finally(() => {
        if (requestId !== mediationsRequestId.current) return;
        setLoading(false);
      });
  }, [version]);

  useEffect(() => {
    loadMediations();
  }, [loadMediations]);

  // Step-1-only selections, per platform — see the identical `mediationSelection` in
  // `AndroidPanel`/`IosPanel` and `mergeSelections`'s comment for why step 2 must never depend on
  // the step-2 response's own choices.
  const androidMediationSelection = useMemo(
    () => deriveSelection(androidMediations, androidChoices),
    [androidMediations, androidChoices]
  );
  const iosMediationSelection = useMemo(
    () => deriveSelection(iosMediations, iosChoices),
    [iosMediations, iosChoices]
  );
  const mediationRequest = useMemo<SdksRequest>(
    () => ({ android: androidMediationSelection, ios: iosMediationSelection }),
    [androidMediationSelection, iosMediationSelection]
  );

  const hasMediations = androidMediations.length > 0 || iosMediations.length > 0;

  // Step 2
  useEffect(() => {
    // Bump the guard on every path that starts a new "attempt" for this step — including the
    // early return below — so a still in-flight response from a previous (now-superseded)
    // attempt can never pass the `requestId === ref.current` check and overwrite state.
    const requestId = ++sdksRequestId.current;
    if (!hasMediations) {
      setResolved(null);
      setAndroidResolvedChoices({});
      setIosResolvedChoices({});
      setStepErrors((prev) => ({ ...prev, sdks: null }));
      return;
    }
    WizardService.UnityWizard.sdks(version, mediationRequest)
      .then((response) => {
        if (requestId !== sdksRequestId.current) return;
        setResolved(response);
        // Fresh response -> fresh defaults; see the identical comment in `AndroidPanel`.
        setAndroidResolvedChoices(defaultChoices(response.android));
        setIosResolvedChoices(defaultChoices(response.ios));
        setStepErrors((prev) => ({ ...prev, sdks: null }));
      })
      .catch((err: any) => {
        if (requestId !== sdksRequestId.current) return;
        // Clear rather than leave stale — but the empty result must not look like a genuine "0
        // networks" answer, so surface the failure via `stepErrors.sdks` same as steps 1 and 3.
        setResolved(null);
        setAndroidResolvedChoices({});
        setIosResolvedChoices({});
        setStepErrors((prev) => ({
          ...prev,
          sdks: stepErrorMessage(err, "Failed to load resolved SDKs"),
        }));
      });
  }, [version, mediationRequest, hasMediations]);

  const androidResolvedSelection = useMemo(
    () => deriveSelection(resolved?.android ?? [], androidResolvedChoices),
    [resolved, androidResolvedChoices]
  );
  const iosResolvedSelection = useMemo(
    () => deriveSelection(resolved?.ios ?? [], iosResolvedChoices),
    [resolved, iosResolvedChoices]
  );

  // What step 3 and `onSelectionChange` actually use.
  const fullRequest = useMemo<SdksRequest>(
    () => ({
      android: mergeSelections(androidMediationSelection, androidResolvedSelection),
      ios: mergeSelections(iosMediationSelection, iosResolvedSelection),
    }),
    [androidMediationSelection, androidResolvedSelection, iosMediationSelection, iosResolvedSelection]
  );

  useEffect(() => {
    onSelectionChange(fullRequest);
  }, [fullRequest, onSelectionChange]);

  // Step 3
  useEffect(() => {
    // Same early-return guard-bump rationale as step 2 above.
    const requestId = ++dependenciesRequestId.current;
    if (!hasMediations) {
      setCode("");
      setStepErrors((prev) => ({ ...prev, dependencies: null }));
      return;
    }
    WizardService.UnityWizard.dependencies(version, fullRequest)
      .then((response) => {
        if (requestId !== dependenciesRequestId.current) return;
        setCode(response);
        setStepErrors((prev) => ({ ...prev, dependencies: null }));
      })
      .catch((err: any) => {
        if (requestId !== dependenciesRequestId.current) return;
        // Same reason as the android/ios panels: a stale Unity XML under a failure alert reads as
        // the answer to the current selection.
        setCode("");
        setStepErrors((prev) => ({
          ...prev,
          dependencies: stepErrorMessage(err, "Failed to load dependencies"),
        }));
      });
  }, [version, fullRequest, hasMediations]);

  // Corrected 2026-09-18 (KI-24): the slot is `mediationKey(item)`, not `MediationInfo.name`.
  // These four stay four separate maps, so the key needs no platform component — see
  // `mediationKey`'s own note.
  const onAndroidChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setAndroidChoices((prev) => ({ ...prev, [itemKey]: versionId }));
  const onIosChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setIosChoices((prev) => ({ ...prev, [itemKey]: versionId }));
  const onAndroidResolvedChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setAndroidResolvedChoices((prev) => ({ ...prev, [itemKey]: versionId }));
  const onIosResolvedChoiceChange = (itemKey: string, versionId: number | undefined) =>
    setIosResolvedChoices((prev) => ({ ...prev, [itemKey]: versionId }));

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {stepErrors.mediations && <Alert type="error" showIcon message={stepErrors.mediations} />}
      <Spin spinning={loading}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Android</Text>
            <MediationSelector
              items={androidMediations}
              choices={androidChoices}
              onChange={onAndroidChoiceChange}
            />
            {/* Step-2 (`sdks`) result, now selectable — see the identical block in `AndroidPanel`. */}
            <MediationSelector
              items={resolved?.android ?? []}
              choices={androidResolvedChoices}
              onChange={onAndroidResolvedChoiceChange}
            />
          </Col>
          <Col span={12}>
            <Text strong>iOS</Text>
            <MediationSelector items={iosMediations} choices={iosChoices} onChange={onIosChoiceChange} />
            <MediationSelector
              items={resolved?.ios ?? []}
              choices={iosResolvedChoices}
              onChange={onIosResolvedChoiceChange}
            />
          </Col>
        </Row>
      </Spin>
      {stepErrors.sdks && <Alert type="error" showIcon message={stepErrors.sdks} />}
      {resolved && (
        <>
          <ResolvedWarnings sdks={resolved.android} />
          <ResolvedWarnings sdks={resolved.ios} />
        </>
      )}
      {stepErrors.dependencies && <Alert type="error" showIcon message={stepErrors.dependencies} />}
      <CodeBlock language="xml">{code}</CodeBlock>
    </Space>
  );
};

/* ---------------------------------------------------------------------------------------------
 * Validator diagnostics panel (T1/T2) — additive only. Never gates the tabs above: rendered
 * alongside them, and a failed coverage fetch degrades to a neutral info Alert without touching
 * the rest of the tree.
 * ------------------------------------------------------------------------------------------- */

const ValidatorPanel: React.FC<{
  coverage: SpmCoverageResponse | null;
  unavailable: boolean;
  mediation?: string;
}> = ({ coverage, unavailable, mediation }) => {
  const spmLink =
    `/${AppRoutes.Wizard.Spm}` + (mediation ? `?mediation=${encodeURIComponent(mediation)}` : "");

  if (unavailable) {
    return (
      <Alert
        type="info"
        showIcon
        message="SPM diagnostics unavailable"
        description="Could not load the SPM consistency check right now — the integration preview above is unaffected."
      />
    );
  }

  if (!coverage) return null;

  // Tagged with `platform` (the one truly stable field available — `CoreSplitResult` itself has no
  // id) rather than the array index, so a keyed `Alert` never gets mismatched with the wrong
  // platform's data across re-renders.
  const v1Results: { platform: "ios" | "android"; result: CoreSplitResult }[] = coverage.v1
    ? (
        [
          coverage.v1.ios ? { platform: "ios" as const, result: coverage.v1.ios } : null,
          coverage.v1.android ? { platform: "android" as const, result: coverage.v1.android } : null,
        ] as const
      ).filter(
        (r): r is { platform: "ios" | "android"; result: CoreSplitResult } => !!r && r.result.isSplit
      )
    : [];

  // Optional/default-safe per the type's own comment — a backend that predates these two fields
  // omits them entirely, and this must degrade to "no detail available", not a crash.
  const uncovered: UncoveredEntity[] = coverage.v2.uncovered ?? [];
  const uncoveredTruncated = coverage.v2.uncoveredTruncated ?? false;

  // `total === 0` must never divide into a `NaN%` `Progress` bar — this covers the one row that
  // can legitimately be all-zero (a store with no ad-network/service SDKs at all yet).
  const anchorRatio = (withAnchor: number, total: number): number =>
    total > 0 ? Math.round((withAnchor / total) * 100) : 0;

  // Optional/default-safe per the type's own comment — a backend that predates this field omits it
  // entirely, and this must degrade to "no detail available", not a crash.
  const outOfScopeNetworks: OutOfScopeNetwork[] = coverage.v2.outOfScopeNetworks ?? [];
  const noSpmPackageNetworks = outOfScopeNetworks.filter((n) => n.reason === "no_spm_package");
  const notInSelectionNetworks = outOfScopeNetworks.filter((n) => n.reason === "not_in_selection");

  const coverageRows: {
    key: string;
    label: string;
    withAnchor: number;
    total: number;
  }[] = [
    {
      key: "mediation",
      label: "Mediation SDKs",
      withAnchor: coverage.v2.mediationSdksWithAnchor,
      total: coverage.v2.totalMediationSdks,
    },
    {
      key: "adapters",
      label: "Adapters",
      withAnchor: coverage.v2.adaptersWithAnchor,
      total: coverage.v2.totalAdapters,
    },
  ];

  return (
    <ResponsiveCard title="SPM Consistency" contentPadding="1rem">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {v1Results.map(({ platform, result }) => (
          <Alert
            key={platform}
            type="warning"
            showIcon
            message={`${platform === "ios" ? "iOS" : "Android"}: core/adapter split detected`}
            description={
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text>
                  {result.adaptersWithAnchor} adapter(s) with an SPM anchor, {result.nonCoreAnchorCount}{" "}
                  non-core anchor(s).
                </Text>
                <Link to={spmLink}>Review / fix in SPM admin</Link>
                {/* `result.message` is the only place this raw engineering string surfaces — kept,
                    not deleted, but demoted out of the Alert's headline (see (2e) in the SDK-562
                    brief) into a collapsed/secondary spot so the ratio + fix-it link read first. */}
                <Collapse
                  ghost
                  size="small"
                  items={[
                    {
                      key: "raw-message",
                      label: (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Engineering detail
                        </Text>
                      ),
                      children: (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {result.message}
                        </Text>
                      ),
                    },
                  ]}
                />
              </Space>
            }
          />
        ))}
        {/* Rule V2 — one row per entity kind, each a with-anchor/total ratio plus a `Progress` bar
            rather than the previous six bare `Statistic`s. The old layout also carried a
            "Network / Service SDKs" row that was structurally always 0/N — those SDKs are covered
            transitively through their bridge adapters, so a direct anchor on the network/service
            SDK itself was never the right thing to count. That row is gone; the adapters row above
            is the real coverage signal, and `outOfScopeNetworks` below names the networks SPM
            cannot reach at all (as opposed to a mere ratio gap). */}
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {coverageRows.map((row) => (
            <Row key={row.key} gutter={16} align="middle">
              <Col span={7}>
                <Text>{row.label}</Text>
              </Col>
              <Col span={4}>
                <Text strong>
                  {row.withAnchor} / {row.total}
                </Text>
              </Col>
              <Col span={13}>
                <Progress percent={anchorRatio(row.withAnchor, row.total)} size="small" />
              </Col>
            </Row>
          ))}
        </Space>
        {/* Rule V2 detail list — only the entities making up the total/with-anchor gap, capped at
            `UNCOVERED_DETAIL_CAP` (100) server-side. Collapsed by default: this is drill-down
            detail, not the headline number above. */}
        {uncovered.length > 0 && (
          <Collapse
            size="small"
            items={[
              {
                key: "uncovered",
                label: `Without SPM anchor (${uncovered.length}${uncoveredTruncated ? "+" : ""})`,
                children: (
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    {uncovered.map((entity) => (
                      <Space key={`${entity.kind}-${entity.id}`} size={8}>
                        <Tag>{entity.kind}</Tag>
                        <Text>{entity.status}</Text>
                        <Text type="secondary" code style={{ fontSize: 12 }}>
                          {entity.version}
                        </Text>
                      </Space>
                    ))}
                    {uncoveredTruncated && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        List capped at 100 entries (UNCOVERED_DETAIL_CAP) — more may exist.
                      </Text>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
        {/* Networks Rule V2 excludes from the ratio entirely — distinct from `uncovered` above,
            and grouped by `reason` since the two cases are different operator problems: a missing
            package can't be fixed by changing the selection, a version mismatch can. */}
        {outOfScopeNetworks.length > 0 && (
          <Collapse
            size="small"
            items={[
              {
                key: "out-of-scope",
                label: `Not in SPM scope (${outOfScopeNetworks.length})`,
                children: (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {noSpmPackageNetworks.length > 0 && (
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          No SPM package published for this network at any version.
                        </Text>
                        {noSpmPackageNetworks.map((network) => (
                          <Space key={network.id} size={8}>
                            <Tag>no_spm_package</Tag>
                            <Text>{network.status}</Text>
                          </Space>
                        ))}
                      </Space>
                    )}
                    {notInSelectionNetworks.length > 0 && (
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Has an SPM package, but this version is not in the current selection.
                        </Text>
                        {notInSelectionNetworks.map((network) => (
                          <Space key={network.id} size={8}>
                            <Tag>not_in_selection</Tag>
                            <Text>{network.status}</Text>
                          </Space>
                        ))}
                      </Space>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
        {v1Results.length === 0 && <Link to={spmLink}>Open SPM admin</Link>}
      </Space>
    </ResponsiveCard>
  );
};

/* ---------------------------------------------------------------------------------------------
 * Top-level component
 * ------------------------------------------------------------------------------------------- */

interface IntegrationPreviewProps {
  /** SdkViewPage: single platform, tab locked to `sdk.platform`, version pinned to `sdk.version`. */
  sdk?: Sdk;
  /** PluginViewPage: Unity always shown; Android/iOS tabs sourced from `sdks`. */
  plugin?: Plugin;
  sdks?: Sdk[];
  /** PluginViewPage's own `sdks` fetch is still in flight. `sdks` starts as `[]` (not `undefined`)
   * while loading, so without this flag "no tabs yet" is indistinguishable from "genuinely no SDK
   * release for this plugin" — suppresses the empty-state message during that window. */
  sdksLoading?: boolean;
}

/** Prefers the platform's mediation-core `Sdk` (category MEDIATION) as the version/status anchor;
 * falls back to the first match if the plugin has no explicit mediation-core row for that platform. */
const platformAnchor = (sdks: Sdk[] | undefined, platform: Platform): Sdk | undefined => {
  if (!sdks) return undefined;
  const forPlatform = sdks.filter((s) => s.platform === platform);
  return forPlatform.find((s) => s.category === SdkCategory.MEDIATION) ?? forPlatform[0];
};

export const IntegrationPreview: React.FC<IntegrationPreviewProps> = (props) => {
  // Controlled so the coverage check below can key off the tab the user is actually looking at
  // instead of guessing from "which selection happens to be non-empty" (Tabs is otherwise
  // uncontrolled and gives no signal about the active tab).
  const [activeTabKey, setActiveTabKey] = useState<string>("");

  // Lifted out of `IosPanel` (which is now controlled for this prop) so the SPM-gating logic below
  // can read it — the CocoaPods/SPM segment, not to be confused with `activeTabKey` above (the
  // Android/iOS/Unity platform tab).
  const [integrationType, setIntegrationType] = useState<IntegrationType>("cocoapods");

  const [androidSelection, setAndroidSelection] = useState<PlatformSelection | null>(null);
  const [iosSelection, setIosSelection] = useState<PlatformSelection | null>(null);
  const [unityRequest, setUnityRequest] = useState<SdksRequest | null>(null);

  const [coverage, setCoverage] = useState<SpmCoverageResponse | null>(null);
  const [coverageUnavailable, setCoverageUnavailable] = useState(false);

  // PluginViewPage always passes `sdks` (possibly `[]` while its own load is in flight);
  // SdkViewPage never does — that's the discriminator between the two call sites.
  const isPluginMode = props.plugin !== undefined || props.sdks !== undefined;

  const androidAnchor = isPluginMode
    ? platformAnchor(props.sdks, Platform.ANDROID)
    : props.sdk?.platform === Platform.ANDROID
    ? props.sdk
    : undefined;

  const iosAnchor = isPluginMode
    ? platformAnchor(props.sdks, Platform.IOS)
    : props.sdk?.platform === Platform.IOS
    ? props.sdk
    : undefined;

  const unityVersion = isPluginMode ? props.plugin?.version : undefined;

  const items: { key: string; label: string; children: React.ReactNode }[] = [];

  if (androidAnchor) {
    items.push({
      key: "android",
      label: "Android",
      children: (
        <AndroidPanel
          version={androidAnchor.version}
          mediation={mediationParam(androidAnchor.status)}
          onSelectionChange={setAndroidSelection}
        />
      ),
    });
  }

  if (iosAnchor) {
    items.push({
      key: "ios",
      label: "iOS",
      children: (
        <IosPanel
          version={iosAnchor.version}
          mediation={mediationParam(iosAnchor.status)}
          integrationType={integrationType}
          onIntegrationTypeChange={setIntegrationType}
          onSelectionChange={setIosSelection}
        />
      ),
    });
  }

  if (isPluginMode && unityVersion) {
    items.push({
      key: "unity",
      label: "Unity",
      children: (
        <UnityPanel version={unityVersion} onSelectionChange={setUnityRequest} />
      ),
    });
  }

  // Keep `activeTabKey` pointing at a real tab: falls back to the first available tab on mount
  // and whenever the current key stops existing (anchors resolving asynchronously, or a tab
  // disappearing). A no-op once `activeTabKey` already matches an existing item, so this does not
  // fight the user's own tab clicks.
  useEffect(() => {
    if (items.length === 0) return;
    if (items.some((item) => item.key === activeTabKey)) return;
    setActiveTabKey(items[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((item) => item.key).join(","), activeTabKey]);

  // `ValidatorPanel` reports SPM anchor coverage for an SPM selection, so it only makes sense for
  // iOS + SPM — CocoaPods and Unity have no SPM selection to report on.
  const showValidator = activeTabKey === "ios" && integrationType === "spm";

  // The coverage check must reflect whichever tab the user is actually looking at — not "whichever
  // selection happens to be non-empty" (a non-empty `unityRequest` would otherwise always win over
  // `androidSelection`/`iosSelection` regardless of which tab is open). SPM is iOS-only — the
  // backend's `checkSelectionCoverage` deliberately ignores `selection.android` — so the Android
  // tab never has a meaningful selection for this check; it must never read as "0% coverage".
  const hasSelection =
    (activeTabKey === "ios" && iosSelection !== null) ||
    (activeTabKey === "unity" && unityRequest !== null);

  // The `android` branch below is only ever reached in a stale-props render just before the
  // coverage effect's own `!showValidator` guard skips the round-trip — its value is never actually
  // sent over the wire, but it's kept (rather than dropped) so `androidSelection` stays a real read,
  // not write-only state. (That guard used to be a narrower `activeTabKey === "android"` check; this
  // comment still named it after the tightening on 2026-09-02, which is exactly the drift that makes
  // a reader hunt for a guard that no longer exists.)
  const coverageRequest = useMemo<SdksRequest>(() => {
    if (activeTabKey === "unity") {
      return {
        android: unityRequest?.android ?? emptyPlatformSelection(),
        ios: unityRequest?.ios ?? emptyPlatformSelection(),
      };
    }
    if (activeTabKey === "android") {
      return { android: androidSelection ?? emptyPlatformSelection(), ios: emptyPlatformSelection() };
    }
    if (activeTabKey === "ios") {
      return { android: emptyPlatformSelection(), ios: iosSelection ?? emptyPlatformSelection() };
    }
    return { android: emptyPlatformSelection(), ios: emptyPlatformSelection() };
  }, [activeTabKey, unityRequest, androidSelection, iosSelection]);

  // The backend's core-SDK lookup for this check must match the root mediation the SPM graph is
  // actually anchored on. SPM is iOS-only, so that's the iOS anchor specifically — NOT
  // `linkMediation` below, which falls back to the android anchor and would be wrong here (e.g. a
  // MAX-rooted iOS page with an appodeal android anchor must still send `mediation=max`).
  // `undefined` correctly means `appodeal` (the backend's default, unchanged).
  const coverageMediation = mediationParam(iosAnchor?.status);

  // Debounced, isolated from the main preview's data flow: a rejected coverage call only ever
  // flips `coverageUnavailable` — it can never throw into / block the tabs below. `ValidatorPanel`
  // is hidden whenever `!showValidator` (CocoaPods, Unity, Android — no SPM selection to report
  // on), so skip the round-trip entirely there rather than firing it and discarding the response.
  const coverageRequestId = useRef(0);
  useEffect(() => {
    const requestId = ++coverageRequestId.current;
    if (!showValidator) return;
    const handle = setTimeout(() => {
      WizardService.SpmCoverage.check(hasSelection ? coverageRequest : undefined, coverageMediation)
        .then((response) => {
          if (requestId !== coverageRequestId.current) return;
          setCoverage(response);
          setCoverageUnavailable(false);
        })
        .catch(() => {
          if (requestId !== coverageRequestId.current) return;
          setCoverageUnavailable(true);
        });
    }, 400);
    return () => clearTimeout(handle);
  }, [coverageRequest, hasSelection, showValidator, coverageMediation]);

  // SPM only exists on iOS — prefer the iOS anchor's engine for the "fix it by hand" deep link.
  const linkMediation = mediationParam(iosAnchor?.status ?? androidAnchor?.status);

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {items.length === 0 ? (
        props.sdksLoading ? null : (
          <Alert type="info" showIcon message="No SDK release available for this platform yet." />
        )
      ) : items.length === 1 ? (
        items[0].children
      ) : (
        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} items={items} />
      )}
      {/* Reports SPM anchor coverage for an SPM selection, so it renders only for iOS + SPM —
          CocoaPods and Unity have no SPM selection to report on. */}
      {showValidator && (
        <ValidatorPanel coverage={coverage} unavailable={coverageUnavailable} mediation={linkMediation} />
      )}
    </Space>
  );
};
